// src/services/recruitmentService.ts
import { supabase } from '../supabase/config';

export interface TeacherQualification {
  id: string;
  staff_id: string;
  subject_code: string;
  subject_name: string;
  year_levels: string[];
  experience_years: number;
  is_specialist: boolean;
  qualifications: string[];
}

export interface RecruitmentNeed {
  id: string;
  department: string;
  position_title: string;
  fte: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  status: 'draft' | 'approved' | 'advertised' | 'interviewing' | 'filled';
  notes?: string;
  created_date: string;
  approved_date?: string;
  filled_date?: string;
  created_by?: string;
  subjects?: Array<{
    subject_code: string;
    subject_name: string;
    year_levels: string[];
  }>;
}

export interface SubjectQualification {
  id: string;
  subject_code: string;
  subject_name: string;
  department: string;
  year_levels: string[];
  required_qualification: string;
  special_requirements?: string;
}

export const recruitmentService = {
  /**
   * Get all teacher qualifications
   */
  async getTeacherQualifications(): Promise<TeacherQualification[]> {
    try {
      const { data, error } = await supabase
        .from('teacher_qualifications')
        .select('*')
        .order('staff_id');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching teacher qualifications:', error);
      return [];
    }
  },

  /**
   * Get qualifications for a specific teacher
   */
  async getTeacherQualificationsByStaffId(staffId: string): Promise<TeacherQualification[]> {
    try {
      const { data, error } = await supabase
        .from('teacher_qualifications')
        .select('*')
        .eq('staff_id', staffId);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching teacher qualifications:', error);
      return [];
    }
  },

  /**
   * Get all teachers qualified for a specific subject
   */
  async getQualifiedTeachersForSubject(subjectCode: string): Promise<TeacherQualification[]> {
    try {
      const { data, error } = await supabase
        .from('teacher_qualifications')
        .select('*')
        .eq('subject_code', subjectCode);

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching qualified teachers:', error);
      return [];
    }
  },

  /**
   * Add or update teacher qualification
   */
  async upsertTeacherQualification(qualification: Partial<TeacherQualification>): Promise<TeacherQualification | null> {
    try {
      const { data, error } = await supabase
        .from('teacher_qualifications')
        .upsert(qualification)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error upserting teacher qualification:', error);
      return null;
    }
  },

  /**
   * Get all recruitment needs
   */
  async getRecruitmentNeeds(): Promise<RecruitmentNeed[]> {
    try {
      // First get recruitment needs
      const { data: needs, error: needsError } = await supabase
        .from('recruitment_needs')
        .select('*')
        .order('created_date', { ascending: false });

      if (needsError) throw needsError;
      if (!needs) return [];

      // Then get subjects for each need
      const { data: subjects, error: subjectsError } = await supabase
        .from('recruitment_subjects')
        .select('*');

      if (subjectsError) throw subjectsError;

      // Group subjects by recruitment_need_id
      const subjectsByNeed = new Map<string, Array<{
        subject_code: string;
        subject_name: string;
        year_levels: string[];
      }>>();

      (subjects || []).forEach(subject => {
        const needId = subject.recruitment_need_id;
        if (!subjectsByNeed.has(needId)) {
          subjectsByNeed.set(needId, []);
        }
        subjectsByNeed.get(needId)!.push({
          subject_code: subject.subject_code,
          subject_name: subject.subject_name,
          year_levels: subject.year_levels
        });
      });

      // Combine needs with their subjects
      return needs.map(need => ({
        ...need,
        subjects: subjectsByNeed.get(need.id) || []
      }));
    } catch (error) {
      console.error('Error fetching recruitment needs:', error);
      return [];
    }
  },

  /**
   * Get recruitment needs by status
   */
  async getRecruitmentNeedsByStatus(status: RecruitmentNeed['status']): Promise<RecruitmentNeed[]> {
    try {
      const allNeeds = await this.getRecruitmentNeeds();
      return allNeeds.filter(need => need.status === status);
    } catch (error) {
      console.error('Error fetching recruitment needs by status:', error);
      return [];
    }
  },

  /**
   * Create a new recruitment need
   */
  async createRecruitmentNeed(
    need: Omit<RecruitmentNeed, 'id' | 'created_date'>,
    subjects: Array<{ subject_code: string; subject_name: string; year_levels: string[] }>
  ): Promise<RecruitmentNeed | null> {
    try {
      // Insert the recruitment need
      const { data: needData, error: needError } = await supabase
        .from('recruitment_needs')
        .insert({
          department: need.department,
          position_title: need.position_title,
          fte: need.fte,
          priority: need.priority,
          status: need.status,
          notes: need.notes,
          approved_date: need.approved_date,
          created_by: need.created_by
        })
        .select()
        .single();

      if (needError) throw needError;
      if (!needData) return null;

      // Insert the subjects for this need
      if (subjects.length > 0) {
        const subjectInserts = subjects.map(subject => ({
          recruitment_need_id: needData.id,
          subject_code: subject.subject_code,
          subject_name: subject.subject_name,
          year_levels: subject.year_levels
        }));

        const { error: subjectsError } = await supabase
          .from('recruitment_subjects')
          .insert(subjectInserts);

        if (subjectsError) throw subjectsError;
      }

      return {
        ...needData,
        subjects
      };
    } catch (error) {
      console.error('Error creating recruitment need:', error);
      return null;
    }
  },

  /**
   * Update recruitment need status
   */
  async updateRecruitmentNeedStatus(
    id: string,
    status: RecruitmentNeed['status'],
    dateField?: { approved_date?: string; filled_date?: string }
  ): Promise<boolean> {
    try {
      const updateData: any = { status };
      if (dateField?.approved_date) updateData.approved_date = dateField.approved_date;
      if (dateField?.filled_date) updateData.filled_date = dateField.filled_date;

      const { error } = await supabase
        .from('recruitment_needs')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating recruitment need status:', error);
      return false;
    }
  },

  /**
   * Get all subject qualifications
   */
  async getSubjectQualifications(): Promise<SubjectQualification[]> {
    try {
      const { data, error } = await supabase
        .from('subject_qualifications')
        .select('*')
        .order('subject_code');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching subject qualifications:', error);
      return [];
    }
  },

  /**
   * Get subject qualification by subject code
   */
  async getSubjectQualification(subjectCode: string): Promise<SubjectQualification | null> {
    try {
      const { data, error } = await supabase
        .from('subject_qualifications')
        .select('*')
        .eq('subject_code', subjectCode)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching subject qualification:', error);
      return null;
    }
  },

  /**
   * Add or update subject qualification
   */
  async upsertSubjectQualification(qualification: Partial<SubjectQualification>): Promise<SubjectQualification | null> {
    try {
      const { data, error } = await supabase
        .from('subject_qualifications')
        .upsert(qualification)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error upserting subject qualification:', error);
      return null;
    }
  }
};
